// Admin Shop Detail Page — /admin/shops/:shopId

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { OrderStatus } from '@notify/shared';

import type { AdminShopDetailData, ShopOrderRow } from '../../services/admin.service';
import { adminService } from '../../services/admin.service';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatTime(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function ownerInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function avgPrepMinutes(orders: ShopOrderRow[]): string {
  const withDeadline = orders.filter((o) => o.prepDeadline && o.createdAt);
  if (withDeadline.length === 0) return '—';
  const avg =
    withDeadline.reduce((sum, o) => {
      const mins =
        (new Date(o.prepDeadline!).getTime() - new Date(o.createdAt).getTime()) / 60_000;
      return sum + mins;
    }, 0) / withDeadline.length;
  return `${Math.round(avg)}m`;
}

// ─── Toast ────────────────────────────────────────────────────────────────────

interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}

function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  function showToast(type: 'success' | 'error', message: string) {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  }

  return { toasts, showToast };
}

function ToastContainer({ toasts }: { toasts: Toast[] }) {
  if (toasts.length === 0) return null;
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg ${
            t.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          }`}
        >
          {t.message}
        </div>
      ))}
    </div>
  );
}

// ─── Status badges ────────────────────────────────────────────────────────────

const ORDER_STATUS_STYLES: Record<string, string> = {
  [OrderStatus.PENDING]: 'bg-gray-100 text-gray-600',
  [OrderStatus.PREPARING]: 'bg-amber-100 text-amber-700',
  [OrderStatus.READY]: 'bg-green-100 text-green-700',
  [OrderStatus.COLLECTED]: 'bg-blue-100 text-blue-700',
  [OrderStatus.CANCELLED]: 'bg-red-100 text-red-500',
};

const ORDER_STATUS_LABELS: Record<string, string> = {
  [OrderStatus.PENDING]: 'Pending',
  [OrderStatus.PREPARING]: 'Preparing',
  [OrderStatus.READY]: 'Ready',
  [OrderStatus.COLLECTED]: 'Collected',
  [OrderStatus.CANCELLED]: 'Cancelled',
};

function OrderStatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        ORDER_STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {ORDER_STATUS_LABELS[status] ?? status}
    </span>
  );
}

const SUB_STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  trial: 'bg-blue-100 text-blue-700',
  grace: 'bg-amber-100 text-amber-700',
  expired: 'bg-red-100 text-red-500',
  cancelled: 'bg-gray-100 text-gray-500',
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function LineSkeleton({ width = 'w-3/4' }: { width?: string }) {
  return <div className={`h-3.5 animate-pulse rounded bg-gray-200 ${width}`} />;
}

function TableSkeleton() {
  return (
    <div className="animate-pulse divide-y divide-gray-100">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex gap-6 px-6 py-3">
          <div className="h-3.5 w-20 rounded bg-gray-200" />
          <div className="h-3.5 w-16 rounded bg-gray-200" />
          <div className="h-3.5 w-24 rounded bg-gray-200" />
          <div className="h-3.5 w-24 rounded bg-gray-200" />
          <div className="h-3.5 w-20 rounded bg-gray-200" />
        </div>
      ))}
    </div>
  );
}

// ─── Input field ──────────────────────────────────────────────────────────────

function Field({
  label,
  value,
  onChange,
  type = 'text',
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-[#374151]">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full rounded-lg border border-gray-200 bg-[#F8FAFC] px-3 py-2 text-sm text-[#0F172A] outline-none focus:border-[#6366F1] focus:ring-1 focus:ring-[#6366F1] transition-colors"
      />
    </div>
  );
}

// ─── Section label ────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-widest text-[#94A3B8]">
      {children}
    </p>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function PinIcon() {
  return (
    <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminShopDetailPage() {
  const { shopId } = useParams<{ shopId: string }>();
  const navigate = useNavigate();
  const { toasts, showToast } = useToast();

  // Detail
  const [detail, setDetail] = useState<AdminShopDetailData | null>(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState('');

  // Shop edit
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Owner edit
  const [editingOwner, setEditingOwner] = useState(false);
  const [editOwnerName, setEditOwnerName] = useState('');
  const [editOwnerEmail, setEditOwnerEmail] = useState('');
  const [editOwnerSaving, setEditOwnerSaving] = useState(false);
  const [editOwnerError, setEditOwnerError] = useState('');

  // Toggle active
  const [toggling, setToggling] = useState(false);

  // QR retire
  const [retiringQR, setRetiringQR] = useState(false);

  // Orders
  const [ordersDate, setOrdersDate] = useState(todayISO);
  const [orders, setOrders] = useState<ShopOrderRow[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');

  const loadDetail = useCallback(async () => {
    if (!shopId) return;
    setDetailLoading(true);
    setDetailError('');
    try {
      const data = await adminService.getShopDetail(shopId);
      setDetail(data);
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : 'Failed to load shop details');
    } finally {
      setDetailLoading(false);
    }
  }, [shopId]);

  const loadOrders = useCallback(async (date: string) => {
    if (!shopId) return;
    setOrdersLoading(true);
    setOrdersError('');
    try {
      const data = await adminService.getShopOrders(shopId, date);
      setOrders(data);
    } catch (err) {
      setOrdersError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  }, [shopId]);

  useEffect(() => { void loadDetail(); }, [loadDetail]);
  useEffect(() => { void loadOrders(ordersDate); }, [loadOrders, ordersDate]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  function openEdit() {
    if (!detail) return;
    setEditName(detail.shop.name);
    setEditLocation(detail.shop.location);
    setEditError('');
    setEditing(true);
  }

  async function handleSaveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!shopId || !detail) return;
    setEditSaving(true);
    setEditError('');
    try {
      const updated = await adminService.updateShop(shopId, {
        name: editName.trim(),
        location: editLocation.trim(),
      });
      setDetail((prev) =>
        prev ? { ...prev, shop: { ...prev.shop, name: updated.name, location: updated.location } } : prev
      );
      setEditing(false);
      showToast('success', 'Shop updated');
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update shop');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleToggle() {
    if (!shopId) return;
    setToggling(true);
    try {
      const updated = await adminService.toggleShopActive(shopId);
      setDetail((prev) =>
        prev ? { ...prev, shop: { ...prev.shop, isActive: updated.isActive } } : prev
      );
      showToast('success', `Shop ${updated.isActive ? 'activated' : 'deactivated'}`);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to toggle');
    } finally {
      setToggling(false);
    }
  }

  function openOwnerEdit() {
    if (!detail?.owner) return;
    setEditOwnerName(detail.owner.name);
    setEditOwnerEmail(detail.owner.email);
    setEditOwnerError('');
    setEditingOwner(true);
  }

  async function handleSaveOwner(e: React.FormEvent) {
    e.preventDefault();
    if (!shopId) return;
    setEditOwnerSaving(true);
    setEditOwnerError('');
    try {
      const updated = await adminService.updateShopOwner(shopId, {
        name: editOwnerName.trim(),
        email: editOwnerEmail.trim(),
      });
      setDetail((prev) => prev ? { ...prev, owner: updated } : prev);
      setEditingOwner(false);
      showToast('success', 'Owner updated');
    } catch (err) {
      setEditOwnerError(err instanceof Error ? err.message : 'Failed to update owner');
    } finally {
      setEditOwnerSaving(false);
    }
  }

  async function handleRetireQR() {
    if (!detail?.qrCode) return;
    setRetiringQR(true);
    try {
      await adminService.retireQRCode(detail.qrCode.id);
      showToast('success', 'QR code retired');
      navigate('/admin/qr-codes');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to retire QR code');
      setRetiringQR(false);
    }
  }

  if (!shopId) {
    return <div className="p-8 text-center text-sm text-[#64748B]">Invalid shop URL.</div>;
  }

  // ─── Derived ─────────────────────────────────────────────────────────────────

  const isActive = detail?.shop.isActive ?? false;
  const sub = detail?.subscription ?? null;
  const daysLeft = daysUntil(sub?.expires_at);
  const avgPrep = avgPrepMinutes(orders);

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-full bg-[#F8FAFC]">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">

        {/* Back link */}
        <Link
          to="/admin/shops"
          className="mb-5 inline-flex items-center gap-1.5 text-[13px] text-[#64748B] hover:text-[#0F172A] transition-colors"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Shops
        </Link>

        {detailError && (
          <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-600">
            {detailError}
          </div>
        )}

        {/* ── Shop header card ─────────────────────────────────────────────────── */}
        <div className="mb-5 rounded-xl border border-gray-200 bg-white px-6 py-5 shadow-sm">
          {detailLoading ? (
            <div className="space-y-2.5">
              <LineSkeleton width="w-48" />
              <LineSkeleton width="w-32" />
              <LineSkeleton width="w-24" />
            </div>
          ) : detail && editing ? (
            <form onSubmit={(e) => void handleSaveEdit(e)} className="space-y-4">
              <p className="text-[13px] font-medium text-[#0F172A]">Edit Shop</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Shop Name" value={editName} onChange={setEditName} required />
                <Field label="Location" value={editLocation} onChange={setEditLocation} required />
              </div>
              {editError && <p className="text-xs text-red-600">{editError}</p>}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={editSaving || !editName.trim() || !editLocation.trim()}
                  className="rounded-lg bg-[#6366F1] px-4 py-2 text-xs font-medium text-white hover:bg-indigo-600 disabled:opacity-60 transition-colors"
                >
                  {editSaving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  disabled={editSaving}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-xs font-medium text-[#374151] hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : detail ? (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-[22px] font-medium leading-snug text-[#0F172A]">
                  {detail.shop.name}
                </h1>
                <div className="mt-1 flex items-center gap-1 text-[13px] text-[#64748B]">
                  <PinIcon />
                  <span>{detail.shop.location}</span>
                </div>
                <p className="mt-1 text-[12px] text-[#94A3B8]">
                  Created {formatDate(detail.shop.createdAt as unknown as string)}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                    isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {isActive ? 'Active' : 'Inactive'}
                </span>
                <button
                  type="button"
                  disabled={toggling}
                  onClick={() => void handleToggle()}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-[12px] font-medium text-[#374151] hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  {toggling ? 'Updating…' : isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  type="button"
                  onClick={openEdit}
                  className="rounded-lg bg-[#6366F1] px-3 py-1.5 text-[12px] font-medium text-white hover:bg-indigo-600 transition-colors"
                >
                  Edit
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {/* ── Two-column grid ──────────────────────────────────────────────────── */}
        <div
          className="mb-5 grid gap-5"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}
        >
          {/* ── Left: Owner + Subscription ──────────────────────────────────── */}
          <div className="rounded-xl border border-gray-200 bg-white px-6 py-5 shadow-sm">

            {/* Owner section */}
            <div className="mb-1 flex items-center justify-between">
              <SectionLabel>Owner</SectionLabel>
              {!detailLoading && detail?.owner && !editingOwner && (
                <button
                  type="button"
                  onClick={openOwnerEdit}
                  className="rounded-md px-2.5 py-1 text-[11px] font-medium text-[#6366F1] hover:bg-[#EEF2FF] transition-colors"
                >
                  Edit
                </button>
              )}
            </div>

            {detailLoading ? (
              <div className="mt-3 space-y-2.5">
                <LineSkeleton width="w-3/4" />
                <LineSkeleton width="w-1/2" />
              </div>
            ) : editingOwner ? (
              <form onSubmit={(e) => void handleSaveOwner(e)} className="mt-3 space-y-3">
                <Field label="Name" value={editOwnerName} onChange={setEditOwnerName} required />
                <Field label="Email" value={editOwnerEmail} onChange={setEditOwnerEmail} type="email" required />
                {editOwnerError && <p className="text-xs text-red-600">{editOwnerError}</p>}
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={editOwnerSaving || !editOwnerName.trim() || !editOwnerEmail.trim()}
                    className="rounded-lg bg-[#6366F1] px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-600 disabled:opacity-60 transition-colors"
                  >
                    {editOwnerSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingOwner(false)}
                    disabled={editOwnerSaving}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-[#374151] hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : detail?.owner ? (
              <div className="mt-3 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#6366F1] text-sm font-semibold text-white">
                  {ownerInitials(detail.owner.name)}
                </div>
                <div className="min-w-0">
                  <p className="text-[15px] font-medium text-[#0F172A]">{detail.owner.name}</p>
                  <p className="truncate text-[13px] text-[#64748B]">{detail.owner.email}</p>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-[13px] text-[#94A3B8]">No owner assigned</p>
            )}

            {/* Divider */}
            <div className="my-5 border-t border-gray-100" />

            {/* Subscription section */}
            <SectionLabel>Subscription</SectionLabel>
            {detailLoading ? (
              <div className="mt-3 space-y-2.5">
                <LineSkeleton width="w-2/3" />
                <LineSkeleton width="w-1/2" />
                <LineSkeleton width="w-3/4" />
              </div>
            ) : sub ? (
              <div className="mt-3 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-[#EEF2FF] px-2.5 py-0.5 text-[11px] font-semibold capitalize text-[#6366F1]">
                    {sub.plan_name}
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize ${
                      SUB_STATUS_STYLES[sub.status] ?? 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {sub.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-[13px]">
                  <div>
                    <p className="text-[11px] text-[#94A3B8]">Subscribed</p>
                    <p className="mt-0.5 text-[#0F172A]">{formatDate(sub.subscribed_at)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-[#94A3B8]">Expires</p>
                    <p className="mt-0.5 text-[#0F172A]">{formatDate(sub.expires_at)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-[#94A3B8]">Days remaining</p>
                    <p
                      className={`mt-0.5 font-medium ${
                        daysLeft === null
                          ? 'text-[#0F172A]'
                          : daysLeft <= 0
                          ? 'text-red-500'
                          : daysLeft <= 7
                          ? 'text-amber-600'
                          : 'text-[#0F172A]'
                      }`}
                    >
                      {daysLeft === null ? '—' : daysLeft <= 0 ? 'Expired' : `${daysLeft} days`}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-[13px] text-[#94A3B8]">No subscription found</p>
            )}
          </div>

          {/* ── Right: QR Code + Today's Stats ──────────────────────────────── */}
          <div className="rounded-xl border border-gray-200 bg-white px-6 py-5 shadow-sm">

            {/* QR Code section */}
            <SectionLabel>QR Code</SectionLabel>
            {detailLoading ? (
              <div className="mt-3 flex gap-3">
                <div className="h-[100px] w-[100px] shrink-0 animate-pulse rounded-lg bg-gray-200" />
                <div className="flex-1 space-y-2 pt-1">
                  <LineSkeleton width="w-16" />
                  <LineSkeleton width="w-full" />
                  <LineSkeleton width="w-full" />
                </div>
              </div>
            ) : detail?.qrCode ? (
              <div className="mt-3 space-y-3">
                <div className="flex items-start gap-3">
                  <img
                    src={detail.qrCode.qrImageUrl}
                    alt="Shop QR code"
                    width={100}
                    height={100}
                    className="shrink-0 rounded-lg border border-gray-200"
                  />
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">
                      Active
                    </span>
                    <p className="truncate font-mono text-[12px] text-[#64748B]">
                      {detail.qrCode.token}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={retiringQR}
                  onClick={() => void handleRetireQR()}
                  className="w-full rounded-lg border border-red-200 py-1.5 text-[12px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-60 transition-colors"
                >
                  {retiringQR ? 'Retiring…' : 'Retire & Regenerate'}
                </button>
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                <p className="text-[13px] text-[#94A3B8]">No QR code linked</p>
                <Link
                  to="/admin/qr-codes"
                  className="inline-flex items-center rounded-lg bg-[#6366F1] px-3 py-1.5 text-[12px] font-medium text-white hover:bg-indigo-600 transition-colors"
                >
                  Go to QR Codes
                </Link>
              </div>
            )}

            {/* Divider */}
            <div className="my-5 border-t border-gray-100" />

            {/* Today's Stats section */}
            <SectionLabel>Today's Stats</SectionLabel>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {[
                {
                  label: 'Orders',
                  value: detailLoading ? null : String(detail?.todayOrderCount ?? 0),
                },
                {
                  label: 'Notified',
                  value: detailLoading ? null : String(detail?.todayNotificationCount ?? 0),
                },
                {
                  label: 'Avg prep',
                  value: ordersLoading ? null : avgPrep,
                },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="rounded-lg bg-[#F8FAFC] px-3 py-3 text-center"
                >
                  {value === null ? (
                    <div className="mx-auto mb-1 h-6 w-10 animate-pulse rounded bg-gray-200" />
                  ) : (
                    <p className="text-[20px] font-semibold leading-none text-[#0F172A]">{value}</p>
                  )}
                  <p className="mt-1 text-[11px] text-[#64748B]">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Orders table card ────────────────────────────────────────────────── */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <SectionLabel>Orders</SectionLabel>
            <input
              type="date"
              value={ordersDate}
              max={todayISO()}
              onChange={(e) => setOrdersDate(e.target.value)}
              className="rounded-lg border border-gray-200 bg-[#F8FAFC] px-3 py-1.5 text-[12px] text-[#0F172A] outline-none focus:border-[#6366F1] focus:ring-1 focus:ring-[#6366F1] transition-colors"
            />
          </div>

          <div className="overflow-x-auto">
            {ordersError ? (
              <p className="px-6 py-8 text-center text-[13px] text-red-600">{ordersError}</p>
            ) : ordersLoading ? (
              <div className="py-3">
                <TableSkeleton />
              </div>
            ) : orders.length === 0 ? (
              <p className="px-6 py-10 text-center text-[13px] text-[#94A3B8]">
                No orders on this date
              </p>
            ) : (
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8]">
                      Reference
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8]">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8]">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8]">
                      Deadline
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8]">
                      Notified
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {orders.map((order) => (
                    <tr key={order.id} className="transition-colors hover:bg-[#F8FAFC]">
                      <td className="px-6 py-3 font-mono font-semibold text-[#0F172A]">
                        {order.orderReference}
                      </td>
                      <td className="px-6 py-3">
                        <OrderStatusBadge status={order.status} />
                      </td>
                      <td className="px-6 py-3 text-[#64748B]">
                        {formatTime(order.createdAt)}
                      </td>
                      <td className="px-6 py-3 text-[#64748B]">
                        {order.prepDeadline ? formatTime(order.prepDeadline) : '—'}
                      </td>
                      <td className="px-6 py-3 text-[#64748B]">
                        {order.notifiedAt ? formatTime(order.notifiedAt) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} />
    </div>
  );
}
